import i18n from "i18next"
import { initReactI18next } from "react-i18next"

// ============================================================
// Переводы EyeHub — namespace-based архитектура
// ============================================================
// Namespaces:
//   common        — навигация, UI, общие действия, языки, title приложения
//   auth          — страница логина (SSO)
//   organizations — организации / площадки / инсталляции / домены
//   connectors    — fleet-коннекторы (identities on-prem инсталляций)
//   enrollment    — enrollment коннекторов (токены, статусы)
//   licenses      — лицензии (issuer)
//   federation    — федеративные связи + директория
//   support       — тикеты, комментарии
//   dashboard     — fleet-обзор
//   discovery     — публичный entry-flow сотрудника (email → org + площадки)
//   mysites       — site-picker залогиненного сотрудника
//   maintenance   — окна планового обслуживания (Ц13)
//   provisioning  — операторский провижининг (hub-admin) + самообслуживание
//                   организации: витрина потребления и переключатель перерасхода
//                   (org-admin, ключи myLicenses.*) — срез 3, ADR §7.8/§7.11
//   errors        — ошибки приложения и сети
//   findclinic    — лендинг пациента: поиск клиники, инвайт, мои клиники
// Feature-страницы дополняют эти namespaces своими ключами.
// ============================================================

// Русские переводы (namespaces)
import commonRU from "./locales/ru/common.json"
import authRU from "./locales/ru/auth.json"
import organizationsRU from "./locales/ru/organizations.json"
import connectorsRU from "./locales/ru/connectors.json"
import enrollmentRU from "./locales/ru/enrollment.json"
import licensesRU from "./locales/ru/licenses.json"
import federationRU from "./locales/ru/federation.json"
import supportRU from "./locales/ru/support.json"
import dashboardRU from "./locales/ru/dashboard.json"
import discoveryRU from "./locales/ru/discovery.json"
import mysitesRU from "./locales/ru/mysites.json"
import maintenanceRU from "./locales/ru/maintenance.json"
import provisioningRU from "./locales/ru/provisioning.json"
import errorsRU from "./locales/ru/errors.json"
import releaseTrustRU from "./locales/ru/releaseTrust.json"
import findclinicRU from "./locales/ru/findclinic.json"

// Английские переводы (namespaces)
import commonEN from "./locales/en/common.json"
import authEN from "./locales/en/auth.json"
import organizationsEN from "./locales/en/organizations.json"
import connectorsEN from "./locales/en/connectors.json"
import enrollmentEN from "./locales/en/enrollment.json"
import licensesEN from "./locales/en/licenses.json"
import federationEN from "./locales/en/federation.json"
import supportEN from "./locales/en/support.json"
import dashboardEN from "./locales/en/dashboard.json"
import discoveryEN from "./locales/en/discovery.json"
import mysitesEN from "./locales/en/mysites.json"
import maintenanceEN from "./locales/en/maintenance.json"
import provisioningEN from "./locales/en/provisioning.json"
import errorsEN from "./locales/en/errors.json"
import releaseTrustEN from "./locales/en/releaseTrust.json"
import findclinicEN from "./locales/en/findclinic.json"

// Ресурсы переводов (поддерживаемые языки)
const resources = {
  ru: {
    common: commonRU,
    auth: authRU,
    organizations: organizationsRU,
    connectors: connectorsRU,
    enrollment: enrollmentRU,
    licenses: licensesRU,
    federation: federationRU,
    support: supportRU,
    dashboard: dashboardRU,
    discovery: discoveryRU,
    mysites: mysitesRU,
    maintenance: maintenanceRU,
    provisioning: provisioningRU,
    errors: errorsRU,
    releaseTrust: releaseTrustRU,
    findclinic: findclinicRU,
  },
  en: {
    common: commonEN,
    auth: authEN,
    organizations: organizationsEN,
    connectors: connectorsEN,
    enrollment: enrollmentEN,
    licenses: licensesEN,
    federation: federationEN,
    support: supportEN,
    dashboard: dashboardEN,
    discovery: discoveryEN,
    mysites: mysitesEN,
    maintenance: maintenanceEN,
    provisioning: provisioningEN,
    errors: errorsEN,
    releaseTrust: releaseTrustEN,
    findclinic: findclinicEN,
  },
}

// Поддерживаемые локали (синхронизировано с backend)
export const SUPPORTED_LOCALES = ["ru", "en"]
export const DEFAULT_LOCALE = "ru"

// Список namespaces
export const NAMESPACES = [
  "common",
  "auth",
  "organizations",
  "connectors",
  "enrollment",
  "licenses",
  "federation",
  "support",
  "dashboard",
  "discovery",
  "mysites",
  "maintenance",
  "provisioning",
  "errors",
  "releaseTrust",
  "findclinic",
]

// Получаем сохранённый язык или используем русский по умолчанию.
// try/catch — модульный код, исключение крашит всё приложение (Safari private).
let savedLanguage = null
try {
  savedLanguage = localStorage.getItem("I18N_LANGUAGE")
} catch (_) { /* storage unavailable */ }

const initialLanguage = (savedLanguage && SUPPORTED_LOCALES.includes(savedLanguage))
  ? savedLanguage
  : DEFAULT_LOCALE

if (!savedLanguage || !SUPPORTED_LOCALES.includes(savedLanguage)) {
  try { localStorage.setItem("I18N_LANGUAGE", DEFAULT_LOCALE) } catch (_) { /* ignore */ }
}

// Синхронизация lang-атрибута HTML-документа с текущей локалью
// (доступность, SEO, формат нативных <input type="date">).
const syncDocumentLang = (lang) => {
  document.documentElement.lang = lang
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLanguage,
    fallbackLng: "ru",

    ns: NAMESPACES,
    defaultNS: "common",

    keySeparator: ".", // ключи вида "status.online"

    interpolation: {
      escapeValue: false, // React уже экранирует XSS
    },
  })

syncDocumentLang(initialLanguage)
i18n.on("languageChanged", syncDocumentLang)

/**
 * Текущая локаль для API-запросов (параметр ?language=).
 */
export const getCurrentApiLocale = () => {
  return i18n.language || DEFAULT_LOCALE
}

export default i18n
